// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';
import logger from '../utils/logger';

// Default categories for fallback
const DEFAULT_INCOME_STATEMENT_CATEGORIES = {
  income: ['Revenue', 'Fees Income'],
  costOfSales: [
    'Cost of Sales - Food',
    'Cost of Sales - Tea/Coffee',
    'Cost of Sales - Other Bev',
    'Cost of Sales - Wine',
    'Cost of Sales - Liquor',
    'Cost of Sales - Beer',
    'Cost of Sales - Retails'
  ],
  laborCost: [
    'Wages - F.O.H. Salaried',
    'Wages - F.O.H. Hourly',
    'Wages - F.O.H. Hourly - OT',
    'Wages - B.O.H. Salaried',
    'Wages - B.O.H. Hourly',
    'Wages - B.O.H. Hourly - OT'
  ],
  employerTaxes: ['Employer Taxes'],
  otherStaffCosts: ['Staff Insurance', 'Contract Labor', 'Other Employee Expenses'],
  operatingExpenses: [
    'Restaurant Operation Supplies',
    'Maintenance',
    'Advertising and Marketing',
    'Other Operating Expenses',
    'General and Administrative Expenses',
    'Travel and Entertainment',
    'Professional Fee',
    'Insurance Expense',
    'Utilities',
    'Occupancy Costs',
    'Uncategorized Expenses'
  ],
  otherCategories: [
    'Net Income',
    'Corporate Tax Expense',
    'Depreciation Expense',
    'Amortization Expense',
    'Interest Expense',
    'Other Expenses',
    'Other Income',
    'Uncategorized'
  ]
};


// Create the context
const TenantContext = createContext(null);

/**
 * TenantProvider component that fetches and provides tenant configuration.
 * 
 * Usage:
 *   <TenantProvider userEmail="user@example.com">
 *     <App />
 *   </TenantProvider>
 */
export function TenantProvider({ userEmail, children }) {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configType, setConfigType] = useState('income_statement');
  const [enabledFeatures, setEnabledFeatures] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(true); // Start true, resolves quickly
  const [featuresError, setFeaturesError] = useState(null); // Connection/fetch error for features

  // Fetch tenant config from backend
  const fetchConfig = useCallback(async (type = 'income_statement') => {
    if (!userEmail) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Get current session for JWT token
      await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/tenant-config/frontend/by-email/${encodeURIComponent(userEmail)}?config_type=${type}`,
        {
          credentials: 'include',
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch tenant config: ${response.status}`);
      }

      const data = await response.json();
      
      setTenantConfig(data);
      setConfigType(type);
      return data;

    } catch (err) {
      if (err.name === 'SessionExpiredError') return null;
      logger.error('Error fetching tenant config:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  // Fetch enabled features for user's tenant (called FIRST for fast UI)
  const fetchFeatures = useCallback(async () => {
    if (!userEmail) {
      setEnabledFeatures([]);
      setFeaturesLoading(false);
      return;
    }

    setFeaturesLoading(true);
    setFeaturesError(null);
    try {
      await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/user/features`,
        {
          credentials: 'include',
          headers: {
            'x-api-key': import.meta.env.VITE_API_KEY,
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setEnabledFeatures(data.features || []);
      } else {
        setFeaturesError(`Server returned ${response.status}`);
      }
    } catch (err) {
      // Don't treat session-expired as a connection error — the re-auth modal handles it
      if (err.name === 'SessionExpiredError') return;
      logger.error('Error fetching features:', err);
      setEnabledFeatures([]);
      setFeaturesError(err.message || 'Failed to connect to server');
    } finally {
      setFeaturesLoading(false);
    }
  }, [userEmail]);

  // Check if user has access to a feature
  const hasFeature = useCallback((featureKey) => {
    return enabledFeatures.includes(featureKey);
  }, [enabledFeatures]);

  // Fetch features FIRST (fast, enables UI), then config in background
  useEffect(() => {
    if (userEmail) {
      // Features first - small payload, enables sidebar immediately
      fetchFeatures();
      // Config in background - larger payload, not needed for initial render
      fetchConfig(configType);
    } else {
      setFeaturesLoading(false);
    }
  }, [userEmail, fetchConfig, fetchFeatures, configType]);

  // Get categories based on tenant config or fallback to defaults
  const getCategories = useCallback((statementType = 'income_statement') => {
    // If we have tenant config with categories, use those
    if (tenantConfig && !tenantConfig.is_default && tenantConfig.categories?.length > 0) {
      return tenantConfig.categories;
    }

    // Otherwise return flat list from defaults (always income statement)
    return Object.values(DEFAULT_INCOME_STATEMENT_CATEGORIES).flat();
  }, [tenantConfig]);

  // Get category groups (for dropdown optgroups)
  const getCategoryGroups = useCallback((statementType = 'income_statement') => {
    // If we have tenant config with custom categories, create simple groups
    if (tenantConfig && !tenantConfig.is_default && tenantConfig.categories?.length > 0) {
      // For custom tenant categories, create a single "Categories" group
      return {
        'Categories': tenantConfig.categories
      };
    }

    // Otherwise return default grouped categories (always income statement)
    return {
      'Income': DEFAULT_INCOME_STATEMENT_CATEGORIES.income,
      'Cost of Sales': DEFAULT_INCOME_STATEMENT_CATEGORIES.costOfSales,
      'Labor Cost': DEFAULT_INCOME_STATEMENT_CATEGORIES.laborCost,
      'Employer Taxes': DEFAULT_INCOME_STATEMENT_CATEGORIES.employerTaxes,
      'Other Staff Costs': DEFAULT_INCOME_STATEMENT_CATEGORIES.otherStaffCosts,
      'Operating Expenses': DEFAULT_INCOME_STATEMENT_CATEGORIES.operatingExpenses,
      'Other': DEFAULT_INCOME_STATEMENT_CATEGORIES.otherCategories
    };
  }, [tenantConfig]);

  // Get category color (for styling badges)
  const getCategoryColor = useCallback((category) => {
    // Check if tenant has custom colors in frontend_config (preferred structure)
    if (tenantConfig?.frontend_config?.categoryColors && tenantConfig.frontend_config.categoryColors[category]) {
      return tenantConfig.frontend_config.categoryColors[category];
    }
    
    // Check if tenant has custom colors in legacy category_colors structure
    if (tenantConfig?.category_colors && tenantConfig.category_colors[category]) {
      return tenantConfig.category_colors[category];
    }

    // Fall back to default color mapping
    return getDefaultCategoryColor(category);
  }, [tenantConfig]);

  // Refresh config (e.g., after saving new categories)
  const refreshConfig = useCallback(async () => {
    return fetchConfig(configType);
  }, [fetchConfig, configType]);

  // Switch config type (income_statement only)
  const switchConfigType = useCallback(async (newType) => {
    setConfigType(newType);
    return fetchConfig(newType);
  }, [fetchConfig]);

  const value = {
    tenantConfig,
    loading,
    error,
    configType,
    getCategories,
    getCategoryGroups,
    getCategoryColor,
    refreshConfig,
    switchConfigType,
    isUsingCustomConfig: tenantConfig && !tenantConfig.is_default,
    enabledFeatures,
    hasFeature,
    featuresLoading,
    featuresError,
    retryFeatures: fetchFeatures
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to access tenant context
 */
export function useTenantConfig() {
  const context = useContext(TenantContext);
  if (!context) {
    // Return a default implementation if used outside provider
    return {
      tenantConfig: null,
      loading: false,
      error: null,
      configType: 'income_statement',
      getCategories: () => {
        return Object.values(DEFAULT_INCOME_STATEMENT_CATEGORIES).flat();
      },
      getCategoryGroups: () => {
        return DEFAULT_INCOME_STATEMENT_CATEGORIES;
      },
      getCategoryColor: getDefaultCategoryColor,
      refreshConfig: () => Promise.resolve(null),
      switchConfigType: () => Promise.resolve(null),
      isUsingCustomConfig: false,
      enabledFeatures: [],
      hasFeature: () => false,
      featuresLoading: false,
      featuresError: null,
      retryFeatures: () => Promise.resolve()
    };
  }
  return context;
}

/**
 * Default category color mapping (used when tenant has no custom colors)
 */
function getDefaultCategoryColor(category) {
  const categoryColors = {
    // Comparative Categories
    'Food Sales': 'comp-food-sales',
    'Beverage & Alcohol Sales': 'comp-bev-sales',
    'Sales Other': 'comp-sales-other',
    'Food COGS': 'comp-food-cogs',
    'Beverage & Alcohol COGS': 'comp-bev-cogs',
    'Other COGS': 'comp-other-cogs',
    'Staff FOH': 'comp-staff-foh',
    'Staff BOH': 'comp-staff-boh',
    'Staff Temporary': 'comp-staff-temp',
    'Staff Management': 'comp-staff-mgmt',
    'Staff On-Costs': 'comp-staff-oncosts',
    'Repairs and Maintenance': 'comp-repairs',
    'Advertising & Marketing': 'comp-marketing',
    'Office Supplies and Stationary': 'comp-office-supplies',
    'Professional Fees': 'comp-prof-fees',

    // Standard Income Statement Categories
    'Revenue': 'revenue',
    'Fees Income': 'fees-income',
    'Cost of Sales - Food': 'cogs-food',
    'Cost of Sales - Tea/Coffee': 'cogs-tea-coffee',
    'Cost of Sales - Other Bev': 'cogs-other-bev',
    'Cost of Sales - Wine': 'cogs-wine',
    'Cost of Sales - Liquor': 'cogs-liquor',
    'Cost of Sales - Beer': 'cogs-beer',
    'Cost of Sales - Retails': 'cogs-retails',
    'Wages - F.O.H. Salaried': 'labor-foh-salaried',
    'Wages - F.O.H. Hourly': 'labor-foh-hourly',
    'Wages - F.O.H. Hourly - OT': 'labor-foh-ot',
    'Wages - B.O.H. Salaried': 'labor-boh-salaried',
    'Wages - B.O.H. Hourly': 'labor-boh-hourly',
    'Wages - B.O.H. Hourly - OT': 'labor-boh-ot',
    'Employer Taxes': 'employer-taxes',
    'Staff Insurance': 'staff-insurance',
    'Contract Labor': 'contract-labor',
    'Other Employee Expenses': 'other-employee-expenses',
    'Restaurant Operation Supplies': 'restaurant-supplies',
    'Maintenance': 'maintenance',
    'Advertising and Marketing': 'advertising-marketing',
    'Other Operating Expenses': 'other-operating',
    'General and Administrative Expenses': 'general-admin',
    'Travel and Entertainment': 'travel-entertainment',
    'Professional Fee': 'professional-fee',
    'Insurance Expense': 'insurance-expense',
    'Utilities': 'utilities',
    'Occupancy Costs': 'occupancy-costs',
    'Uncategorized Expenses': 'uncategorized-expenses',
    'Net Income': 'net-income',
    'Corporate Tax Expense': 'corporate-tax',
    'Depreciation Expense': 'depreciation',
    'Amortization Expense': 'amortization',
    'Interest Expense': 'interest-expense',
    'Other Expenses': 'other-expenses',
    'Other Income': 'other-income',
    'Uncategorized': 'uncategorized',

    // Balance Sheet Categories
    'Cash Equivalent': 'bs-cash',
    'Undeposited Funds': 'bs-undeposited',
    'Accounts Receivable': 'bs-accounts-receivable',
    'Inventory': 'bs-inventory',
    'Prepaid Expenses': 'bs-prepaid-expenses',
    'Prepaid Insurance': 'bs-prepaid',
    'Suspense Account': 'bs-suspense',
    'Fees': 'bs-fees',
    'Leasehold Improvement': 'bs-leasehold',
    'Kitchen/Hood': 'bs-kitchen',
    'Machinery and Equipment': 'bs-machinery',
    'Furniture and Fixtures': 'bs-furniture',
    'Intangible Assets': 'bs-intangible',
    'Start-Up Cost': 'bs-startup',
    'Security Deposits': 'bs-security',
    'Liquor License': 'bs-liquor-license',
    'Investments': 'bs-investments',
    'Accounts Payable': 'bs-accounts-payable',
    'Credit Card Liabilities': 'bs-credit-card',
    'Accrued Liabilities': 'bs-accrued-liabilities',
    'Accrued Insurance': 'bs-accrued',
    'Intercompany': 'bs-intercompany',
    'Loans Payable': 'bs-loans',
    'Long Term Debt': 'bs-long-term-debt',
    "Member's Contributions/(Distributions)": 'bs-contributions',
    "Member's Equity": 'bs-equity'
  };

  return categoryColors[category] || 'custom-category';
}

// Export defaults for use in components that might not have context
export const DEFAULT_CATEGORIES = {
  income_statement: DEFAULT_INCOME_STATEMENT_CATEGORIES
};

export default TenantContext;

